import { z } from 'zod';
import { StayFolioSummary } from './folio.js';
import { AuditedFields, CursorPageQuery, IsoDateTime, Uuid, Version } from './common.js';
import { CustomerSummary } from './customers.js';
import { PricingUnit, ResourceSummary } from './resources.js';
/** Machine d'état (D-002, DOMAIN_ARCHITECTURE §4). */
export const ReservationStatus = z.enum([
    'DRAFT',
    'PENDING_CONFIRMATION',
    'OPTION',
    'CONFIRMED',
    'CHECKED_IN',
    'CHECKED_OUT',
    'CANCELLED',
    'NO_SHOW',
]);
/** Statuts qui verrouillent l'inventaire (unit_locks). */
export const BLOCKING_RESERVATION_STATUSES = [
    'OPTION',
    'CONFIRMED',
    'CHECKED_IN',
];
export const ReservationSource = z.enum([
    'DIRECT',
    'PHONE',
    'WHATSAPP',
    'EMAIL',
    'WEBSITE',
    'BOOKING_COM',
    'AIRBNB',
    'CORPORATE',
    'REFERRAL',
    'OTHER',
]);
export const ComplianceStatus = z.enum(['NOT_REQUIRED', 'INCOMPLETE', 'COMPLETE']);
export const DiscountType = z.enum(['NONE', 'PERCENT', 'AMOUNT']);
export const ReservationLineKind = z.enum(['ACCOMMODATION', 'EXTRA', 'FEE', 'DISCOUNT']);
export const ReservationLine = z.object({
    id: Uuid,
    kind: ReservationLineKind,
    label: z.string(),
    description: z.string().nullable(),
    quantity: z.number().positive(),
    unit: PricingUnit.nullable(),
    unitPrice: z.number(),
    amount: z.number(),
    taxRuleCodes: z.array(z.string()),
    position: z.number().int(),
});
export const ReservationLineInput = z.object({
    kind: ReservationLineKind.exclude(['ACCOMMODATION']),
    label: z.string().min(1).max(160),
    description: z.string().max(500).nullable().optional(),
    quantity: z.number().positive().default(1),
    unitPrice: z.number(),
});
export const Occupant = z.object({
    id: Uuid,
    personId: Uuid.nullable(),
    fullName: z.string(),
    isMain: z.boolean(),
});
export const OccupantInput = z.object({
    personId: Uuid.nullable().optional(),
    fullName: z.string().min(1).max(160),
    isMain: z.boolean().default(false),
});
export const ReservationTotals = z.object({
    currencyCode: z.string().length(3),
    subtotal: z.number(),
    discountTotal: z.number(),
    taxTotal: z.number(),
    withholdingTotal: z.number(),
    net: z.number(),
    paid: z.number(),
    balance: z.number(),
    /** Avance enregistrée avant confirmation (montant libre, D-022). */
    depositReceived: z.number(),
    /** Charges actives du folio de séjour, déjà comprises dans `subtotal` et `net` (ADR-018). */
    folioTotal: z.number().default(0),
});
export const Reservation = z
    .object({
    id: Uuid,
    reference: z.string(),
    status: ReservationStatus,
    propertyId: Uuid,
    resource: ResourceSummary.extend({ typeName: z.string(), capacityMax: z.number().int() }),
    guest: CustomerSummary,
    billingParty: CustomerSummary,
    /** Entreprise liée au séjour (peut différer du facturé). */
    company: CustomerSummary.nullable(),
    arrivalAt: IsoDateTime,
    departureAt: IsoDateTime,
    nights: z.number().int().nullable(),
    adults: z.number().int().nonnegative(),
    children: z.number().int().nonnegative(),
    occupants: z.array(Occupant),
    pricingUnit: PricingUnit,
    unitPrice: z.number(),
    quantity: z.number().positive(),
    ratePlanId: Uuid.nullable(),
    priceOverridden: z.boolean(),
    discountType: DiscountType,
    discountValue: z.number(),
    /** Retenue à la source 5 % retenue sur ce séjour (D-012) — intention de l'opérateur. */
    applyWithholding: z.boolean(),
    lines: z.array(ReservationLine),
    totals: ReservationTotals,
    /** Folio de séjour (ADR-018) : `null` tant qu'aucune charge n'a été posée. */
    folio: StayFolioSummary.nullable().default(null),
    source: ReservationSource,
    agentUserId: Uuid.nullable(),
    agentName: z.string().nullable(),
    optionExpiresAt: IsoDateTime.nullable(),
    earlyCheckinRequested: z.boolean(),
    lateCheckoutGranted: z.boolean(),
    confirmedWithoutDeposit: z.boolean(),
    compliance: z.object({ status: ComplianceStatus, missing: z.array(z.string()) }),
    cancellation: z
        .object({ reason: z.string(), cancelledAt: IsoDateTime, feeAmount: z.number() })
        .nullable(),
    checkedInAt: IsoDateTime.nullable(),
    checkedOutAt: IsoDateTime.nullable(),
    notesInternal: z.string().nullable(),
    notesGuest: z.string().nullable(),
    version: Version,
})
    .merge(AuditedFields);
export const ReservationListItem = Reservation.pick({
    id: true,
    reference: true,
    status: true,
    resource: true,
    guest: true,
    billingParty: true,
    arrivalAt: true,
    departureAt: true,
    nights: true,
    adults: true,
    children: true,
    totals: true,
    source: true,
    optionExpiresAt: true,
    compliance: true,
    createdAt: true,
    updatedAt: true,
});
/** Création (flux UX en 3 blocs : séjour, client, tarif). */
export const CreateReservationRequest = z
    .object({
    propertyId: Uuid.optional(),
    resourceId: Uuid,
    arrivalAt: IsoDateTime,
    departureAt: IsoDateTime,
    adults: z.number().int().min(1).default(1),
    children: z.number().int().nonnegative().default(0),
    occupants: z.array(OccupantInput).default([]),
    guestId: Uuid,
    billingPartyId: Uuid.optional(),
    companyId: Uuid.nullable().optional(),
    /** Plan tarifaire choisi ; sinon le plan par défaut de la ressource. */
    ratePlanId: Uuid.nullable().optional(),
    /** Prix unitaire révisé (permission reservation.override_price, D-022). */
    unitPrice: z.number().nonnegative().optional(),
    pricingUnit: PricingUnit.optional(),
    quantity: z.number().positive().optional(),
    discountType: DiscountType.default('NONE'),
    discountValue: z.number().nonnegative().default(0),
    /**
     * Appliquer la retenue à la source 5 % (D-012). Non renseigné : la valeur
     * par défaut de l'entreprise facturée (`withholdingDefault`) s'applique.
     */
    applyWithholding: z.boolean().optional(),
    lines: z.array(ReservationLineInput).default([]),
    source: ReservationSource.default('DIRECT'),
    earlyCheckinRequested: z.boolean().default(false),
    notesInternal: z.string().max(4000).nullable().optional(),
    notesGuest: z.string().max(2000).nullable().optional(),
    /** Statut initial : brouillon (sans verrou), option (verrou + expiration) ou en attente. */
    initialStatus: z.enum(['DRAFT', 'PENDING_CONFIRMATION', 'OPTION']).default('DRAFT'),
    optionExpiresAt: IsoDateTime.nullable().optional(),
})
    .refine((v) => new Date(v.departureAt) > new Date(v.arrivalAt), {
    message: 'Le départ doit être postérieur à l’arrivée',
    path: ['departureAt'],
});
export const UpdateReservationRequest = z.object({
    resourceId: Uuid.optional(),
    arrivalAt: IsoDateTime.optional(),
    departureAt: IsoDateTime.optional(),
    adults: z.number().int().min(1).optional(),
    children: z.number().int().nonnegative().optional(),
    occupants: z.array(OccupantInput).optional(),
    guestId: Uuid.optional(),
    billingPartyId: Uuid.optional(),
    companyId: Uuid.nullable().optional(),
    ratePlanId: Uuid.nullable().optional(),
    unitPrice: z.number().nonnegative().optional(),
    pricingUnit: PricingUnit.optional(),
    quantity: z.number().positive().optional(),
    discountType: DiscountType.optional(),
    discountValue: z.number().nonnegative().optional(),
    /** Non renseigné : la valeur retenue à la création est conservée (D-012). */
    applyWithholding: z.boolean().optional(),
    lines: z.array(ReservationLineInput).optional(),
    source: ReservationSource.optional(),
    earlyCheckinRequested: z.boolean().optional(),
    lateCheckoutGranted: z.boolean().optional(),
    notesInternal: z.string().max(4000).nullable().optional(),
    notesGuest: z.string().max(2000).nullable().optional(),
    optionExpiresAt: IsoDateTime.nullable().optional(),
    version: Version,
});
/** Transitions (DOMAIN_ARCHITECTURE §4). */
export const HoldReservationRequest = z.object({ optionExpiresAt: IsoDateTime, version: Version });
export const ConfirmReservationRequest = z.object({
    /** Requis sauf permission reservation.confirm.without_deposit (R-013). */
    withoutDepositReason: z.string().min(10).max(500).optional(),
    version: Version,
});
export const CancelReservationRequest = z.object({
    reason: z.string().min(5).max(500),
    feeAmount: z.number().nonnegative().default(0),
    version: Version,
});
export const NoShowReservationRequest = z.object({
    feeAmount: z.number().nonnegative().default(0),
    version: Version,
});
export const CheckInRequest = z.object({
    checkedInAt: IsoDateTime.optional(),
    keysHanded: z.boolean().default(true),
    arrivalNotes: z.string().max(2000).nullable().optional(),
    occupants: z.array(OccupantInput).optional(),
    version: Version,
});
export const CheckOutRequest = z.object({
    checkedOutAt: IsoDateTime.optional(),
    departureNotes: z.string().max(2000).nullable().optional(),
    /** Requis si solde > 0 et permission reservation.checkout.with_balance (R-010). */
    withBalanceReason: z.string().min(10).max(500).optional(),
    version: Version,
});
export const ReservationHistoryEntry = z.object({
    id: Uuid,
    fromStatus: ReservationStatus.nullable(),
    toStatus: ReservationStatus.nullable(),
    action: z.string(),
    reason: z.string().nullable(),
    actorName: z.string().nullable(),
    occurredAt: IsoDateTime,
    metadata: z.record(z.unknown()).nullable(),
});
export const ListReservationsQuery = CursorPageQuery.extend({
    propertyId: Uuid.optional(),
    status: z.string().optional(),
    q: z.string().max(100).optional(),
    resourceId: Uuid.optional(),
    guestId: Uuid.optional(),
    billingPartyId: Uuid.optional(),
    arrivalFrom: IsoDateTime.optional(),
    arrivalTo: IsoDateTime.optional(),
    departureFrom: IsoDateTime.optional(),
    departureTo: IsoDateTime.optional(),
    balanceDue: z.coerce.boolean().optional(),
    sort: z.string().optional(),
});
/** Devis instantané (pré-remplissage du bloc tarif avant création). */
export const QuoteRequest = z.object({
    resourceId: Uuid,
    arrivalAt: IsoDateTime,
    departureAt: IsoDateTime,
    billingPartyId: Uuid.optional(),
    ratePlanId: Uuid.nullable().optional(),
    unitPrice: z.number().nonnegative().optional(),
    pricingUnit: PricingUnit.optional(),
    discountType: DiscountType.default('NONE'),
    discountValue: z.number().nonnegative().default(0),
    lines: z.array(ReservationLineInput).default([]),
    applyWithholding: z.boolean().optional(),
});
export const Quote = z.object({
    pricingUnit: PricingUnit,
    quantity: z.number(),
    unitPrice: z.number(),
    ratePlanId: Uuid.nullable(),
    ratePlanName: z.string().nullable(),
    lines: z.array(ReservationLine.omit({ id: true })),
    totals: ReservationTotals.omit({ paid: true, balance: true, depositReceived: true }),
    taxDetails: z.array(z.object({
        code: z.string(),
        label: z.string(),
        kind: z.enum(['TAX', 'WITHHOLDING']),
        rate: z.number(),
        base: z.number(),
        amount: z.number(),
    })),
});
/** Envoi de confirmation (canaux extensibles). */
export const SendConfirmationRequest = z.object({
    channels: z.array(z.enum(['EMAIL', 'WHATSAPP'])).min(1),
    to: z.string().max(200).optional(),
    message: z.string().max(2000).optional(),
});
//# sourceMappingURL=reservations.js.map